const _toString = Object.prototype.toString;

/**
 * 描述 返回数据类型
 * @date 2022-01-25
 * @param {any} obj
 * @returns {any}
 */
const dataType = obj => _toString.call(obj).slice(8, -1);

/**
 * 描述 是否为对象
 * @date 2022-01-25
 * @param {any} obj
 * @returns {boolean}
 */
const isObject = obj => obj !== null && typeof obj === "object";

/**
 * 描述 是否为对象
 * @date 2022-01-25
 * @param {any} obj
 * @returns {boolean}
 */
const isPlainObject = obj => obj && dataType(obj) === "Object";

/**
 * 描述 是否是空对像
 * @date 2022-01-25
 * @param {any} value
 * @returns {boolean}
 */
const isEmptyObject = obj => {
  if (!obj) { return false; }
  return isPlainObject(obj) && JSON.stringify(obj) === "{}";
};

/**
 * 描述 是否是数组
 * @date 2022-01-26
 * @param {Array} arr
 * @returns {boolean}
 */
const isArray = arr => arr && dataType(arr) === "Array";

/**
 * 描述 是否是二位数组
 * @date 2022-01-26
 * @param {Array} arr
 * @returns {boolean}
 */
const is2dArray = arr => arr.every(item => Array.isArray(item));

/**
 * 描述 是否是字符串
 * @date 2022-01-26
 * @param {string} str
 * @returns {boolean}
 */
const isString = str => str && dataType(str) === "String";

/**
 * 描述 是否是数字
 * @date 2022-01-26
 * @param {number} num
 * @returns {boolean}
 */
const isNumber = num => num && dataType(num) === "Number";

/**
 * 描述 是否是布尔值
 * @date 2022-01-26
 * @param {boolean} bool
 * @returns {boolean}
 */
const isBoolean = bool => bool && dataType(bool) === "Boolean";

/**
 * 描述 兼容flat
 * @date 2022-01-25
 * @param {array} arr
 * @returns {array}
 */

const flattenDeep = arr => arr.reduce((acc, val) => (Array.isArray(val) ? acc.concat(flattenDeep(val)) : acc.concat(val)), []);

/**
 * 描述 对象是否存在某给属性
 * @date 2022-01-27
 * @param {object} obj
 * @param {string} key
 * @returns {boolean}
 */
// eslint-disable-next-line no-prototype-builtins
const hasOwnProperty = (obj, key) => obj.hasOwnProperty(key);

/**
 * 描述 扁平化嵌套数组
 * @date 2022-01-25
 * @param {array} arr
 * @param {number} deep=1
 * @returns {array}
 */
const flat = (arr, deep = 1) => {
  try {
    return arr.flat(deep);
  } catch (error) {
    return flattenDeep(arr);
  }
};

/**
 * 描述 注册组件
 * @date 2022-02-07
 * @param {array} fileList： 文件列表
 * @param {boolean} isWebpack=true： 是否是使用webpack打包生成
 * @param {array} list：引入的组件列表（不是webpack）
 * @param {function} call：自定义路径分析
 * @param {string} type='/'： 默认分隔符
 * @param {number} typeIndex=1：获取第几个数据
 * @returns {object}
 */
const registerComponents = ({ fileList, isWebpack = true, list, call, type = "/", typeIndex = 1, filter = [] }) => {
  const obj = {};
  const arr = isWebpack ? fileList.keys() : list;
  arr.map(item => {
    if (isWebpack) {
      const name = item.split(type)[typeIndex].replace(/\.\w+$/, "");
      const conf = fileList(item);
      const component = conf.default || conf;
      if (!filter.includes(name)) { obj[name] = component; }
    } else {
      call(item, obj);
    }
  });
  return obj;
};

/**
 * 描述：递归(复杂度更高，支持范围更广)
 * @date 2022-01-25
 * @param {array} {arr: 数据源
 * @param {function} sCall：子数据遍历前的回调
 * @param {function} cCall：子数据回调
 * @param {function} eCall：子数据遍历后的回调
 * @param {array} children: 子数据数组
 * @param {object} opt：数据对像
 * @param {array} res：父级数组
 * @param {number} count}：表示层数变量
 * @param {array|Object} data：自定义处理的数据返回值
 * @returns {array}
 */
const recursiveData = ({ arr, sCall, cCall, eCall, children = "children", opt, res, i, data }) => {
  if (!arr || !arr.length) { return arr; }
  return arr.map((item, index) => {
    const child = item[children];
    const newI = i + 1;
    sCall && sCall({ item, res, index, i, newI, data });
    if (child && child.length) {
      item[children] = recursiveData({ arr: item[children], sCall, cCall, eCall, children, opt, res: item, i: newI, data });
      cCall && cCall({ item, res, index, i, newI, data });
    }
    eCall && eCall({ item, res, index, i, newI, data });
    return item;
  });
};

/**
 * 描述 递归
 * @date 2022-01-25
 * @param {array} list：递归的数据源
 * @param {object} opt：参数
 * @param {number} i=0：当前层数
 * @returns {array}
 */
const recursiveFunc = (list, opt, i = 0) => {
  const { name = "children", call, isReturn = false } = opt;
  if (!list.length) { return list; }
  return list.map(item => {
    if (isReturn) { item = call && call(item, i); }
    if (!isReturn) { call && call(item, i); }
    if (item[name] && item[name].length > 0) { item[name] = recursiveFunc(item[name], opt, i + 1); }
    return item;
  });
};

/**
 * 描述 某一字符是否存在当前字符串
 * @date 2022-01-25
 * @param {string} {str
 * @param {number} start
 * @param {number} end
 * @param {string} word}
 * @returns {string}
 */
const strHasWord = ({ str, start, end, word }) => {
  const _index = str.substring(start, end).toLowerCase().indexOf(word);
  return _index !== 0;
};

/**
 * 描述 合并2个对象，并根据参数过滤
 * @date 2022-01-25
 * @param {object} {source
 * @param {object} target
 * @param {array} filter=[]
 * @param {boolean} isReturn=true}
 * @returns {object}
 */
const createObject = ({ source, target, filter = [], isReturn = true, isFAll = false }) => {
  const obj = {};
  const data = isFAll ? { ...source, ...target } : target;
  const keysList = Object.keys(data) || [];
  const filterKeys = keysList.filter(stem => !filter.includes(stem));
  if (!isFAll) { Object.assign(obj, { ...source }); }
  // eslint-disable-next-line no-return-assign
  filterKeys.forEach(item => obj[item] = isFAll ? data[item] : target[item]);
  if (isReturn) { return obj; }
};

/**
 * 描述 是否是json数据(标准json)
 * @date 2022-01-26
 * @param {any} data
 * @returns {boolean}
 */
const isJson = data => {
  if (isString(data)) {
    try {
      const obj = JSON.parse(data);
      return obj && isPlainObject(obj);
    } catch (error) {
      return false;
    }
  } else {
    return false;
  }
};

/**
 * 描述 是否是同一条数据
 * @date 2022-01-26
 * @param {any} a
 * @param {any} b
 * @returns {boolean}
 */
const isSameData = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * 描述 处理下载中文乱码
 * @date 2022-01-27
 * @param {any} response 需要处理的返回的数据
 * @param {any} fn=encodeURI
 * @param {any} splitType=';'
 * @returns {any}
 */
const downloadFilename = (response, fn = encodeURI, splitType = ";") => {
  if (!response.headers["content-disposition"]) { return response; }
  const regName = /^filename/;
  const cds = response.headers["content-disposition"];
  const cdsArr = cds.split(splitType);
  const str = cdsArr.find(item => regName.test(item));
  const strIndex = cdsArr.findIndex(item => regName.test(item));
  const filename = str.split("=");
  const newFileText = fn(filename[1]);

  cdsArr[strIndex] = `${filename[0]}=${newFileText}`;
  response.headers["content-disposition"] = cdsArr.join(splitType);
  return response;
};

/**
 * 描述首字母大小写
 * @date 2022-01-27
 * @param {string} str
 * @param {string} type='Upper'
 * @returns {string}
 */
const strFirstWith = (str, type = "Upper") => {
  const zh = /^[\u4E00-\u9FA5]+$/;
  if (zh.test(str) || !str) { return str; }
  return str.charAt(0)[`to${type}Case`]() + str.slice(1);
};

const setLoading = ({ self, loading, state = true, cancelLoading }) => {
  !cancelLoading && self && loading && self.load && self.$set(self.load, loading, state);
};

/**
 * 描述 过滤外层空数据
 * @date 2022-02-21
 * @param {object} data
 * @returns {object}
 */
const filterEmpty = (data, bool = true) => {
  const obj = {};
  for (const key in data) {
    const val = data[key];
    if (val || [0, false].includes(val) || !bool) { obj[key] = val; };
  }
  return obj;
};

/**
 * 描述 接口公共处理方法
 * @date 2022-01-28
 * @param {function} {api: 接口
 * @param {object} params：参数
 * @param {function} call：成功回调、回调
 * @param {function} error：失败回调
 * @param {boolean} isData}：是否序列化
 * @returns {null}
 */
const getApi = ({ api, otherParams, params, call, error, isData, self, loading, name, isTip = false, cancelLoading = false }) => {
  const msg = self.apiTip && self.apiTip[name] || isTip;
  setLoading({ self, loading, cancelLoading });
  const other = otherParams && otherParams();
  const apiResult = other ? api(other, params, isData) : api(params, isData);

  apiResult.then(res => {
    if (!hasOwnProperty(res, "success") || res.success) {
      call && call(res.data, res, "call");
      msg && self.$message.success(msg.success || self.$t("common.deleteSuccess"));
    } else {
      error && error(res.data, res, "error");
      msg && self.$message.error(msg.error || self.$t("common.deleteError"));
    }
  }).finally(() => {
    setLoading({ self, loading, state: false, cancelLoading });
  });
};

/**
 * 描述 替换当前对象的属性
 * @date 2022-01-28
 * @param {object} source
 * @param {object} transform
 * @returns {object}
 */
const replaceFields = (source, transform) => {
  if (isEmptyObject(source) || !source) { return source; }
  const keys = Object.keys(source) || [];
  const transformKeys = Object.keys(transform) || [];
  const obj = {};
  // eslint-disable-next-line no-return-assign
  keys.forEach(item => obj[transformKeys.includes(item) ? transform[item] : item] = source[item]);
  return obj;
};

/**
 * 描述 跟据字段生成字符串
 * @date 2022-01-29
 * @param {object} {obj
 * @param {array} arr
 * @param {string} type=''}
 * @returns {string}
 */
const createField = ({ obj, arr, type = "" }) => {
  let str = "";
  arr.map(item => {
    if (obj[item]) { str += obj[item].trim() + type; }
  });
  return str;
};

/**
 * 描述 查找数组中指定数据是否存在
 * @date 2022-02-08
 * @param {array} {data
 * @param {string} type='type'
 * @param {string} value}
 * @returns {object}
 */
const filterColumns = ({ data, type = "type", value, isFilter = false }) => {
  if (!data || !data.length) { return []; }
  return isFilter ? data.filter(item => item[type]) : data.find(item => item[type] === value);
};

/**
 * 描述 检测当前数据
 * @date 2022-02-08
 * @param {array} {data=[]
 * @param {string} rowKey}
 * @returns {object}
 */
const indexOf = ({ data = [], key }) => {
  const arr = data && data.length ? [...data] : [];
  const index = arr.indexOf(key);
  if (index === -1) {
    arr.push(key);
  } else {
    arr.splice(1, index, key);
  }
  return {
    arr,
    index
  };
};

/**
 * 描述 事件处理方法
 * @date 2022-02-11
 * @param {object} {self
 * @param {object} args
 * @param {object} obj
 * @param {string} name='confirm'
 * @param {function} call}
 * @returns {any}
 */
const emitFun = ({ self, name = "confirm", call, emit = true, suffix = "" }, args, obj) => {
  const { item, index } = args;
  if (self[item.prop + suffix]) { self[item.prop](item, index, obj); };
  call && call({ self, args, obj, name });
  emit && self.$emit(name, item, index, obj);
};

const toHump = (str, type = "-") => {
  if (!str || !str.includes(type)) { return str; };
  const toArr = str.split(type);
  if (!toArr.length) { return str; };
  if (toArr.length > 1) {
    const newArr = toArr.slice(1).map(item => strFirstWith(item));
    return toArr[0] + newArr.split("");
  } else {
    return toArr[0];
  }
};

/**
 * 描述 将字符串更新成驼峰式
 * @date 2022-02-17
 * @param {string} str
 * @param {string} source='-'
 * @param {string} result=''
 * @returns {string}
 */
const toTypeStr = (str, source = "-", result = "") => {
  if (!str) { return str; };
  const attr = str.split(source).map((item, index) => {
    item = item && index !== 0 ? strFirstWith(item) : item;
    return item;
  });
  return attr.join(result);
};

/**
 * 描述 大写转小写
 * @date 2022-02-14
 * @param {any} ''
 * @returns {any}
 */

const upperToLower = (str, type = "-") => str.split("").map(item => (/^[A-Z]/.test(item) ? `${type}${item.toLowerCase()}` : item)).join("");

/**
 * 描述 将前一个对象的值赋值给后面对象
 * @date 2022-02-23
 * @param {object} source
 * @param {object} target
 * @returns {object}
 */
const createTargetObj = (source, target) => {
  const keys = Object.keys(target) || [];
  keys.map(item => {
    target[item] = source ? source[item] : target[item];
  });
};

// 判断是否为当前页最后一条数据
// 更新页数
export function updatePageNum(deleteRows, pagination) {
  const deleteAfterTotal = pagination.total - deleteRows.length;
  const num = deleteAfterTotal % pagination.pageSize;
  if (pagination.pageNum > 1 && num === 0) {
    pagination.pageNum--;
  }
  return pagination;
}
export {
  _toString,
  dataType,
  isObject,
  isPlainObject,
  isEmptyObject,
  isArray,
  is2dArray,
  isString,
  isNumber,
  isBoolean,
  hasOwnProperty,
  flat,
  registerComponents,
  recursiveData,
  recursiveFunc,
  strHasWord,
  createObject,
  isJson,
  isSameData,
  downloadFilename,
  strFirstWith,
  filterEmpty,
  getApi,
  replaceFields,
  createField,
  filterColumns,
  indexOf,
  emitFun,
  toHump,
  toTypeStr,
  upperToLower,
  createTargetObj
};
